import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ReportsContext from 'contexts/ReportsContext';
import { buildReportShow } from 'navigation/appRoutes';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import Button from 'kingpin/atoms/Button';
import { Option } from 'kingpin/atoms/ContextMenu';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Search from 'components/Search';
import AccessInput from 'components/AccessInput';
import useLiveGadgetCopySettings from 'hooks/useLiveGadgetCopySettings';
import usePopup from 'hooks/usePopup';
import STORE from 'store';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import withoutNulls from 'api/expression/withoutNulls';
import getIdentifier from 'getIdentifier';
import isRemindersGadget from 'types/visTypeCheckers/isRemindersGadget';
import isEntityInformationTable from 'types/visTypeCheckers/isEntityInformationTable';
import {
  buildNewReportSettings,
  buildReportGadgetFromSettings,
} from 'utils/copyGadgetSettings';

const List = styled.div`
  margin-bottom: 24px;
  height: 400px;
  overflow-y: auto;
  padding-right: 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  height: 32px;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

const CopyDashboardGadgetToReportButton = ({
  chartDef,
}: {
  chartDef: VisualisationDefinition;
}) => {
  const { availableReports } = useContext(ReportsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedPortal } = useContext(PortalsContext);
  const navigate = useNavigate();
  const { isOpen, open, close } = usePopup(false, undefined, true);
  const { settings, w, h } = useLiveGadgetCopySettings();

  const [step, setStep] = useState<1 | 2>(1);
  const [copyTo, setCopyTo] = useState<
    'new report' | 'existing report' | undefined
  >(undefined);
  const [searchText, setSearchText] = useState('');
  const [selectedReport, setSelectedReport] = useState<ReportType | undefined>(
    undefined,
  );
  const [title, setTitle] = useState('');
  const [access, setAccess] = useState<ResourceAccess>({
    version: '2',
    type: 'Private',
  });
  const [isLoading, setIsLoading] = useState(false);

  const filteredReports = useMemo(() => {
    if (searchText === '') {
      return availableReports;
    }
    const lower = searchText.toLowerCase();
    return availableReports.filter((r) => r.name.toLowerCase().includes(lower));
  }, [availableReports, searchText]);

  const radioOptions: RadioOption[] = useMemo(
    () => [
      {
        key: 'new report',
        label: 'New Report',
        isSelected: copyTo === 'new report',
        onSelected: () => setCopyTo('new report'),
        testId: 'copy-to-new-report',
      },
      {
        key: 'existing report',
        label: 'Existing Report',
        isSelected: copyTo === 'existing report',
        onSelected: () => setCopyTo('existing report'),
        testId: 'copy-to-existing-report',
      },
    ],
    [copyTo],
  );

  const resetState = useCallback(() => {
    setStep(1);
    setCopyTo(undefined);
    setSearchText('');
    setSelectedReport(undefined);
    setTitle('');
    setAccess({
      version: '2',
      type: 'Private',
    });
    setIsLoading(false);
  }, []);

  const onClose = useCallback(() => {
    close();
    resetState();
  }, [close, resetState]);

  const reportChartDef = useMemo((): typeof chartDef => {
    if (isEntityInformationTable(chartDef)) {
      return {
        ...chartDef,
        rows: chartDef.rows.map((row) => {
          const { reportDrillDownId: _r, boardDrillDownId: _b, ...rest } = row;
          return rest as typeof row;
        }),
      };
    }
    return chartDef;
  }, [chartDef]);

  const saveGadgetToReport = useCallback(
    async (targetReport: ReportType) => {
      setIsLoading(true);
      const now = DateTime.utc().toISO();
      const newGadgetId = getIdentifier();
      const newGadget = buildReportGadgetFromSettings({
        id: newGadgetId,
        chartDef: reportChartDef,
        settings,
        currentUserId,
        now,
      });

      await STORE.visualisations
        .getReportGadgetsRef({ accountId: selectedAccountId })
        .doc(newGadget.id)
        .set(withoutNulls(newGadget));

      const cards = targetReport.canvas.cards;
      const card: CanvasCard.ReportGadgetCard = {
        layout: buildCardLayout(
          0,
          findStartingY(cards, 'desktop'),
          undefined,
          w,
          h,
        ),
        content: {
          type: 'Report Gadget',
          reportGadgetId: newGadget.id,
        },
      };

      const newCanvas: Canvas = {
        ...targetReport.canvas,
        cards: [...cards, card],
      };

      const updatedReport: ReportType = {
        ...targetReport,
        canvas: newCanvas,
        updatedOn: now,
        updatedBy: currentUserId,
      };

      await STORE.getReportsRef({ accountId: selectedAccountId })
        .doc(targetReport.id)
        .set(withoutNulls(updatedReport));

      setIsLoading(false);
      onClose();
      navigate(
        buildReportShow({
          id: targetReport.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      );
    },
    [
      reportChartDef,
      currentUserId,
      h,
      navigate,
      onClose,
      selectedAccountId,
      selectedPortal,
      settings,
      w,
    ],
  );

  const createNewReport = useCallback(async () => {
    setIsLoading(true);
    const now = DateTime.utc().toISO();
    const newGadgetId = getIdentifier();
    const newReportId = getIdentifier();

    const newGadget: ReportGadgets.ReportGadget = {
      id: newGadgetId,
      chartDef: reportChartDef,
      createdBy: currentUserId,
      createdOn: now,
      updatedBy: currentUserId,
      updatedOn: now,
    };

    await STORE.visualisations
      .getReportGadgetsRef({ accountId: selectedAccountId })
      .doc(newGadget.id)
      .set(newGadget);

    const card: CanvasCard.ReportGadgetCard = {
      layout: buildCardLayout(0, 0, undefined, w, h),
      content: {
        type: 'Report Gadget',
        reportGadgetId: newGadget.id,
      },
    };

    const newReport: ReportType = {
      version: '7',
      id: newReportId,
      name: title,
      ...buildNewReportSettings(settings),
      canvas: {
        cards: [card],
      },
      access,
      isV3Published: false,
      v3Access: {
        isPublic: false,
        groupIds: [],
        userIds: [],
      },
      labelIds: [],
      createdBy: currentUserId,
      createdOn: now,
      updatedBy: currentUserId,
      updatedOn: now,
    };

    await STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(newReport.id)
      .set(withoutNulls(newReport));

    setIsLoading(false);
    onClose();
    navigate(
      buildReportShow({
        id: newReport.id,
        isEditing: true,
        portal: selectedPortal,
      }),
    );
  }, [
    access,
    reportChartDef,
    currentUserId,
    h,
    navigate,
    onClose,
    selectedAccountId,
    selectedPortal,
    settings,
    title,
    w,
  ]);

  const onCopyClicked = useCallback(() => {
    if (copyTo === 'new report') {
      setStep(2);
    } else if (copyTo === 'existing report') {
      setStep(2);
    }
  }, [copyTo]);

  const onSaveClicked = useCallback(async () => {
    if (copyTo === 'new report') {
      await createNewReport();
    } else if (copyTo === 'existing report' && selectedReport) {
      await saveGadgetToReport(selectedReport);
    }
  }, [copyTo, createNewReport, saveGadgetToReport, selectedReport]);

  const onBackClicked = useCallback(() => {
    setStep(1);
    setSearchText('');
    setSelectedReport(undefined);
    setTitle('');
  }, []);

  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const onTitleChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(event.target.value);
    },
    [],
  );

  // Don't render for Reminders gadgets
  if (isRemindersGadget(chartDef)) {
    return null;
  }

  return (
    <React.Fragment>
      <Option
        label={'Copy to Report'}
        icon={'copy'}
        onClick={open}
        shouldHideInsteadOfCloseMenu
      />
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={onClose}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            {step === 1 && (
              <Form data-testid="copy-dashboard-gadget-to-report-flow">
                <FormHeader title={'Copy gadget to'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>
                      <Typography.Body type={'Label'}>
                        Destination
                      </Typography.Body>
                    </div>
                    <Inputs.Radio options={radioOptions} />
                  </div>
                </FormContent>
                <Row
                  style={{
                    justifyContent: 'flex-end',
                    marginBottom: 18,
                  }}
                  centerAlign
                >
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        onClick={onClose}
                        size="Small"
                        type="Ghost"
                        label="Cancel"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      label="Copy"
                      onClick={onCopyClicked}
                      isDisabled={copyTo === undefined}
                    />
                  </Row>
                </Row>
              </Form>
            )}
            {step === 2 && copyTo === 'new report' && (
              <Form>
                <FormHeader title={'Create Report'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Body type="Label">
                      Report Title (max. 40 characters)
                    </Typography.Body>
                    <Inputs.TextInput
                      value={title}
                      onChange={onTitleChanged}
                      data-testid={'report-title'}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <AccessInput
                      access={access}
                      setAccess={setAccess}
                      canSetToPrivate={true}
                    />
                  </div>
                </FormContent>
                <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
                  <Button
                    onClick={onClose}
                    isDisabled={isLoading}
                    type="Ghost"
                    size="Small"
                    label="Cancel"
                  />
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        type="Ghost"
                        size="Small"
                        onClick={onBackClicked}
                        isDisabled={isLoading}
                        label="Back"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      label="Save"
                      onClick={onSaveClicked}
                      isLoading={isLoading}
                      isDisabled={title === '' || isLoading}
                    />
                  </Row>
                </Row>
              </Form>
            )}
            {step === 2 && copyTo === 'existing report' && (
              <Form data-testid="copy-dashboard-gadget-to-report-flow">
                <FormHeader title={'Select Report'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <Search
                      noBorder
                      onSearchTextChanged={onSearchTextChanged}
                      searchText={searchText}
                      onClearClicked={() => {
                        setSearchText('');
                      }}
                    />
                  </div>
                  <List>
                    {filteredReports.map((r) => (
                      <Item
                        key={r.id}
                        onClick={() => {
                          if (selectedReport && selectedReport.id === r.id) {
                            setSelectedReport(undefined);
                          } else {
                            setSelectedReport(r);
                          }
                        }}
                      >
                        <Row
                          centerAlign
                          style={{
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <Typography.Body type="Body 12">
                            {r.name}
                          </Typography.Body>
                          {!!selectedReport && selectedReport.id === r.id && (
                            <Icon icon="checkmark" />
                          )}
                        </Row>
                      </Item>
                    ))}
                  </List>
                </FormContent>
                <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
                  <Button
                    onClick={onClose}
                    isDisabled={isLoading}
                    label="Cancel"
                    size="Small"
                    type="Ghost"
                  />
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        onClick={onBackClicked}
                        isDisabled={isLoading}
                        label="Back"
                        size="Small"
                        type="Ghost"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      onClick={onSaveClicked}
                      isDisabled={isLoading || !selectedReport}
                      isLoading={isLoading}
                      label="Save"
                    />
                  </Row>
                </Row>
              </Form>
            )}
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

export default CopyDashboardGadgetToReportButton;
